import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, FileText, MapPin, Calendar, Flag, ClipboardCheck, Wrench } from 'lucide-react';

const STATUS_COLORS = {
  'Open - Unassigned': 'bg-slate-100 text-slate-700',
  'Assigned': 'bg-blue-100 text-blue-800',
  'Scheduled': 'bg-indigo-100 text-indigo-800',
  'Dispatched': 'bg-amber-100 text-amber-800',
  'En Route': 'bg-amber-100 text-amber-800',
  'In Progress': 'bg-yellow-100 text-yellow-800',
  'Field Completion Submitted': 'bg-teal-100 text-teal-800',
  'Completed': 'bg-green-100 text-green-800',
};

const WorkOrderList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { fetchWorkOrders(); /* eslint-disable-next-line */ }, []);

  const fetchWorkOrders = async () => {
    try {
      const res = await axios.get(`${API}/work-orders`, { headers: { Authorization: `Bearer ${token}` } });
      // Show the active service queue (dispatched/assigned work); hide anything still unassigned.
      setWorkOrders((res.data || []).filter((w) => w.status !== 'Open - Unassigned'));
    } catch { toast.error('Failed to load work orders / No se pudieron cargar las órdenes'); }
  };

  const view = async (id) => {
    try {
      const res = await axios.get(`${API}/work-orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedWO(res.data); setOpen(true);
    } catch { toast.error('Failed to load details / No se pudieron cargar los detalles'); }
  };

  const statusBadge = (s) => <Badge className={STATUS_COLORS[s] || 'bg-slate-100 text-slate-700'}>{s}</Badge>;
  const photoSrc = (p) => `${API}/work-orders/${selectedWO.id}/photo/${p.id || p}?auth=${token}`;

  return (
    <div data-testid="field-wo-list">
      <div className="mb-4">
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Work Orders / Mis Órdenes de Trabajo</h2>
        <p className="text-sm text-slate-600">Work dispatched to you / Trabajo asignado a usted</p>
      </div>

      <div className="space-y-3">
        {workOrders.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No work orders dispatched to you yet.</p>
            <p className="text-slate-500 text-sm">Aún no se le han asignado órdenes de trabajo.</p>
          </CardContent></Card>
        ) : (
          workOrders.map((wo) => (
            <Card key={wo.id} className="hover:shadow-md transition-shadow border-l-4 border-[#8FC93A]" data-testid="wo-list-item">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base">{wo.tfs_work_order_number}</CardTitle>
                    <p className="text-sm text-slate-600 truncate">{wo.customer_name} — {wo.service_location}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => view(wo.id)} data-testid="view-wo-details-btn"><Eye className="h-4 w-4" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between"><span className="text-slate-500">Status / Estado</span>{statusBadge(wo.status)}</div>
                  <div className="flex items-center justify-between"><span className="text-slate-500">Priority / Prioridad</span><span className="font-medium">{wo.priority_level}</span></div>
                  <div className="flex items-center justify-between"><span className="text-slate-500">Scheduled / Programado</span><span className="font-medium">{wo.scheduled_date ? _fmtDate(wo.scheduled_date) : '—'}{wo.service_window ? ` · ${wo.service_window}` : ''}</span></div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="field-wo-detail">
          <DialogHeader><DialogTitle>Work Order / Orden de Trabajo</DialogTitle></DialogHeader>
          {selectedWO && (
            <div className="space-y-4">
              <div className="rounded-lg bg-[#0E2A47] text-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold">{selectedWO.tfs_work_order_number}</div>
                  {statusBadge(selectedWO.status)}
                </div>
                <div className="mt-1 text-sm text-white/80">{selectedWO.job_name}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-400">Customer / Cliente</div><div className="font-medium">{selectedWO.customer_name}</div></div>
                <div><div className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} /> Location / Ubicación</div><div className="font-medium">{selectedWO.service_location}</div></div>
                <div className="sm:col-span-2"><div className="text-xs text-slate-400">Address / Dirección</div><div className="font-medium">{selectedWO.service_address || '—'}</div></div>
                <div><div className="text-xs text-slate-400 flex items-center gap-1"><Flag size={11} /> Priority / Prioridad</div><div className="font-medium">{selectedWO.priority_level}</div></div>
                <div><div className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={11} /> Scheduled / Programado</div><div className="font-medium">{selectedWO.scheduled_date ? _fmtDate(selectedWO.scheduled_date) : '—'}{selectedWO.service_window ? ` · ${selectedWO.service_window}` : ''}</div></div>
              </div>

              <div>
                <div className="text-xs text-slate-400 flex items-center gap-1"><Wrench size={11} /> Approved Work Scope / Alcance aprobado</div>
                <div className="mt-1 p-3 bg-slate-50 rounded border text-sm">{selectedWO.service_scope || selectedWO.description_of_work || '—'}</div>
              </div>

              {selectedWO.special_instructions && (
                <div><div className="text-xs text-slate-400">Special Instructions / Instrucciones especiales</div>
                  <div className="mt-1 p-3 bg-slate-50 rounded border text-sm">{selectedWO.special_instructions}</div></div>
              )}

              {(selectedWO.before_photos || []).length > 0 && (
                <div><div className="text-xs text-slate-400 mb-1">Before Photos / Fotos antes</div>
                  <div className="grid grid-cols-3 gap-2">{selectedWO.before_photos.map((p, i) => (
                    <img key={i} src={photoSrc(p)} alt="" className="w-full h-24 object-cover rounded border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ))}</div></div>
              )}
              {(selectedWO.after_photos || []).length > 0 && (
                <div><div className="text-xs text-slate-400 mb-1">After Photos / Fotos después</div>
                  <div className="grid grid-cols-3 gap-2">{selectedWO.after_photos.map((p, i) => (
                    <img key={i} src={photoSrc(p)} alt="" className="w-full h-24 object-cover rounded border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ))}</div></div>
              )}

              {selectedWO.dispatched_at && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="field-dispatch-note">
                  Dispatched to your queue / Enviado a su lista · {_fmtDate(selectedWO.dispatched_at)}
                  {selectedWO.external_email_status === 'unavailable' && ' — external email notification unavailable in Preview (queue delivery only).'}
                </div>
              )}

              {selectedWO.status !== 'Completed' && selectedWO.status !== 'Field Completion Submitted' && (
                <Button className="w-full h-12 bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] text-base font-bold"
                  onClick={() => { setOpen(false); navigate(`/field/work-order/complete?workOrderId=${selectedWO.id}`); }}
                  data-testid="field-submit-completion-btn">
                  <ClipboardCheck size={18} className="mr-2" /> Submit Completion to Management / Enviar finalización
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderList;
